import { loadGoogleFont } from "utils/googleFonts";
import "resources/styles/template1.css";
import { Form, FormInstance } from "antd";
import { useEffect } from "react";
import useGetLogo from "../hooks/useGetLogo";
import { sanitizeHtml } from "utils/sanitizeHtml";

export default function Template1({
  form,
  newFontsOptions,
  companyData,
  companyBankAccount: _companyBankAccount,
}: {
  form: FormInstance;
  newFontsOptions: any;
  companyData: {
    company: GetCompanyWithAddressCompany;
    address: AddressResult;
  };
  companyBankAccount?: BankAccountResult;
}): React.ReactElement {
  const logoDescription = Form.useWatch(["logoDescription"], form);
  const tableBody = Form.useWatch(["tableBorder"], form);
  const tableHeaderColor = Form.useWatch(["baseTextColorBackground"], form);
  const watermarkOpacity = Form.useWatch(["backgroundImageOpacity"], form);
  const baseColor = Form.useWatch(["baseColor"], form);
  const baseTextColor = Form.useWatch(["baseTextColor"], form);
  const titleColor = Form.useWatch(["titleColor"], form);
  const font = Form.useWatch(["font"], form);
  const showName = Form.useWatch(["showName"], form);
  const showDescription = Form.useWatch(["showDescription"], form);
  const showStampSpace = Form.useWatch(["showStampSpace"], form);
  const showMentionAndLegalStatus = Form.useWatch(["showMentionAndLegalStatus"], form);
  const logo = Form.useWatch(["logo"], form);
  const hasLogo = logo?.file?.status !== "uploading";
  const note = Form.useWatch(["note"], form);
  const latePaymentPenalty = Form.useWatch(["latePaymentPenalty"], form);
  const fixedRecoveryFee = Form.useWatch(["fixedRecoveryFee"], form);
  const discount = Form.useWatch(["discount"], form);
  const backgroundImage = Form.useWatch(["backgroundImage"], form);
  const logoPosition = Form.useWatch(["logoPosition"], form);
  const fontName = newFontsOptions?.find((item: { value: unknown }) => item?.value === font)?.label
    ?.props?.children;
  const showDeliveryAddress = Form.useWatch(["showDeliveryAddress"], form);
  const address = companyData?.address;
  const company = companyData?.company;
  const { generateByLogo, accountingFirmName } = useGetLogo();

  const isIntreprenneur = company?.legalCategory?.code === "1000";

  useEffect(() => {
    if (fontName) {
      loadGoogleFont(fontName);
    }
  }, [fontName]);

  const fontStyle = fontName ? { fontFamily: `"${fontName}", sans-serif` } : {};

  return (
    <div className={`A4Container1 logo-position1-${logoPosition || "left"}`} style={fontStyle}>
      {backgroundImage && (
        <div
          className="background-image1"
          style={{
            opacity: watermarkOpacity || 0.1,
          }}
        >
          <img src={backgroundImage} alt="" />
        </div>
      )}
      <header className="header-section1" style={{ borderBottomColor: baseColor || "#e5e7eb" }}>
        <div className="logo-area1">
          {hasLogo ? (
            <img src={logo} alt="" />
          ) : (
            <div className="logo-placeholder" style={{ borderColor: baseColor || "#e0e0e0" }}>
              <span style={{ color: baseTextColor || "#000" }}>LOGO</span>
            </div>
          )}
          <div className="logo-description1" style={{ color: titleColor || "#000", ...fontStyle }}>
            {logoDescription}
          </div>
        </div>
        <div className="invoice-info1">
          <h1 className="invoice-title1" style={{ color: titleColor || "#000", ...fontStyle }}>
            FACTURE
          </h1>
          <p className="invoice-subtitle1" style={{ color: titleColor || "#000", ...fontStyle }}>
            <b>Facture n°:</b> F-123456789
          </p>
          <p className="invoice-subtitle1" style={{ color: titleColor || "#000", ...fontStyle }}>
            <b>Date:</b> {new Date().toLocaleDateString("fr-FR")}
          </p>
        </div>
      </header>
      <hr
        style={{
          backgroundColor: baseColor || "#000",
          border: "2px solid transparent",
          margin: "0",
          padding: "0",
        }}
      />
      <div className="default-header-company-customer">
        <div className="company-section">
          <div className="company-address" style={{ color: baseTextColor || "#000" }}>
            <p>{company?.displayName || company?.name}</p>
            <p>{address?.address1 || ""}</p>
            <p>{address?.address2 || ""}</p>
            <p>
              {address?.postalCode || ""} {address?.city || ""} {address?.country?.name || ""}
            </p>
          </div>
          <div className="company-key_value" style={{ color: baseTextColor || "#000" }}>
            <p>
              <span>TVA INTRACOM:</span>
              <span>{company?.euVatNumber}</span>
            </p>
            <p>
              <span>SIRET:</span>
              <span>{company?.vatNumber}</span>
            </p>
            <p>
              <span>CODE APE:</span>
              <span>{company?.businessActivity?.code}</span>
            </p>
            <p>
              <span>FORME JURIDIQUE:</span>
              <span>{company?.legalStructure?.code}</span>
            </p>
            <p>
              <span>DATE:</span>
              <span>{new Date().toLocaleDateString("fr-FR")}</span>
            </p>
            <p>
              <span>RCS:</span>
              <span>{company?.rcs}</span>
            </p>
          </div>
        </div>
        <div className="customer-section">
          <div className="main-address" style={{ color: baseTextColor || "#000" }}>
            <p>ACME CORPORATION</p>
            <p>123 BUSINESS AVENUE</p>
            <p>SUITE 456</p>
            <p>75001 PARIS, FRANCE</p>
          </div>
          {showDeliveryAddress && (
            <div className="delivery-address" style={{ color: baseTextColor || "#000" }}>
              <p className="delivery-title">ADRESSE DE LIVRAISON:</p>
              <p>ACME CORPORATION - ENTREPÔT</p>
              <p>789 AVENUE DE LA LOGISTIQUE</p>
              <p>ZONE INDUSTRIELLE</p>
              <p>75019 PARIS, FRANCE</p>
            </div>
          )}

          <div className="company-key_value" style={{ color: baseTextColor || "#000" }}>
            <p>
              <span>Nº CLIENT:</span>
              <span>CLI-789123456</span>
            </p>
            <p>
              <span>SIRET:</span>
              <span>12345678901234</span>
            </p>
            <p>
              <span>TVA INTRACOM:</span>
              <span>FR12345678901</span>
            </p>
          </div>
        </div>
      </div>
      <section
        className="default-body1"
        style={{ minHeight: "0px", color: baseTextColor || "#000" }}
      >
        {/* Extra Info */}
        {showName && (
          <small className="extra-info-name1" style={{ color: baseTextColor || "#000" }}>
            Refonte site web e-commerce
          </small>
        )}
        {showDescription && (
          <small className="extra-info-description1" style={{ color: baseTextColor || "#000" }}>
            Développement complet d&apos;une plateforme e-commerce avec système de paiement intégré
            et interface d&apos;administration avancée
          </small>
        )}

        <div className={`default-table1 body-${tableBody || "border_none"}`}>
          <table
            style={{
              borderColor: baseColor || "#000",
            }}
          >
            <thead
              id="customColumnHeader"
              className="default-table__thead1"
              style={{
                backgroundColor: tableHeaderColor || "#ddd",
                color: titleColor || "#000",
                borderColor: baseColor || "#000",
              }}
            >
              <tr style={{ borderColor: baseColor || "#000" }}>
                <td
                  colSpan={1}
                  className="default-table__column-left1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <b>REF</b>
                </td>
                <td
                  colSpan={3}
                  className="default-table__column-left1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <b>DÉSIGNATION</b>
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <b>PU HT</b>
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <b>QTÉ</b>
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <b>UNITÉ</b>
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <b>MONTANT HT</b>
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <b>TVA %</b>
                </td>
              </tr>
            </thead>
            <tbody style={{ color: baseTextColor || "#000" }}>
              <tr className="row" style={{ borderColor: baseColor || "#000" }}>
                <td
                  colSpan={1}
                  className="line-description default-default__lines__column-left1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <span>SERV-001</span>
                </td>
                <td
                  colSpan={3}
                  className="default-table__column-left1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  Consultation développement web
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  150.00
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  8
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  HEURE
                </td>
                <td
                  id="td-vatPct"
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  1,200.00
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  20.00%
                </td>
              </tr>
              <tr className="row" style={{ borderColor: baseColor || "#000" }}>
                <td
                  colSpan={1}
                  className="line-description default-default__lines__column-left1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <span>PROD-002</span>
                </td>
                <td
                  colSpan={3}
                  className="default-table__column-left1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  Ordinateur portable professionnel
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  899.00
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  2
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  UNITAIRE
                </td>
                <td
                  id="td-vatPct"
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  1,798.00
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  20.00%
                </td>
              </tr>
              <tr className="row" style={{ borderColor: baseColor || "#000" }}>
                <td
                  colSpan={1}
                  className="line-description default-default__lines__column-left1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  <span>LIC-003</span>
                </td>
                <td
                  colSpan={3}
                  className="default-table__column-left1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  Licence logiciel annuelle
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  450.00
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  1
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  UNITAIRE
                </td>
                <td
                  id="td-vatPct"
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  450.00
                </td>
                <td
                  colSpan={1}
                  className="default-table__column-right1"
                  style={{ borderColor: baseColor || "#000" }}
                >
                  20.00%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <hr className="custom-divider" style={{ backgroundColor: baseColor || "#000" }} />
        <div className="invoice-bottom-section">
          <div className="default-payments" style={{ color: baseTextColor || "#000" }}>
            <div className="bank-info-section">
              <table className="bank-info-table">
                <tbody>
                  <tr>
                    <td className="default-payments__row">
                      <b>MODE DE PAIEMENT:</b>
                    </td>
                    <td>
                      <span>BANCAIRE</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="default-payments__row">
                      <b>ECHÉANCE:</b>
                    </td>
                    <td>
                      <span>{new Date().toLocaleDateString("fr-FR")}</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="default-payments__row">
                      <b>COORDONNEES BANCAIRES:</b>
                    </td>
                    <td></td>
                  </tr>
                  <tr>
                    <td className="default-payments__row">
                      <b>NOM BANQUE:</b>
                    </td>
                    <td>
                      <span>Crédit Agricole Provence Côte d'Azur</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="default-payments__row">
                      <b>BIC/SWIFT:</b>
                    </td>
                    <td>
                      <span>AGRIFRPP881</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="default-payments__row">
                      <b>IBAN:</b>
                    </td>
                    <td>
                      <span>FR76 1881 9000 1234 5678 9012 345</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="invoice-totals">
            <table className="default-default__totals" style={{ color: baseTextColor || "#000" }}>
              <tbody>
                <tr>
                  <td>
                    <h5 style={{ color: baseTextColor || "#000" }}>TOTAL HT</h5>
                  </td>
                  <td>
                    <h5 style={{ color: baseTextColor || "#000" }}>
                      <span>€3,448.00</span>
                    </h5>
                  </td>
                </tr>
                <tr>
                  <td>
                    <h5 style={{ color: baseTextColor || "#000" }}>TOTAL HT NET</h5>
                  </td>
                  <td>
                    <h5 style={{ color: baseTextColor || "#000" }}>
                      <span>€3,448.00</span>
                    </h5>
                  </td>
                </tr>
                <tr>
                  <td>
                    <h5 style={{ color: baseTextColor || "#000" }}>TVA 20%</h5>
                  </td>
                  <td>
                    <h5 style={{ color: baseTextColor || "#000" }}>
                      <span>€689.60</span>
                    </h5>
                  </td>
                </tr>
                <tr className="total-ttc" style={{ borderTopColor: baseColor || "#000" }}>
                  <td>
                    <h5 style={{ color: baseTextColor || "#000" }}>TOTAL TTC</h5>
                  </td>
                  <td>
                    <h5 style={{ color: baseTextColor || "#000" }}>
                      <span>€4,137.60</span>
                    </h5>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        {showStampSpace && (
          <div className="stamp-space-section" style={{ borderColor: baseColor || "#000" }}>
            <span>Cachet et signature</span>
          </div>
        )}
        <div className="default-terms-and-conditions" style={{ color: baseTextColor || "#000" }}>
          <table>
            <tbody>
              <tr>
                {showMentionAndLegalStatus && (
                  <td
                    style={{ borderColor: baseColor || "#000" }}
                    colSpan={1}
                    className="payment-penalty-terms"
                  >
                    <span>
                      Article L441-6 du code du commerce, tout retard de paiement donnera lieu à
                      l’application d’une pénalité égale à trois fois le taux d’intérêt légal. En
                      sus des pénalités de retard, une indemnité forfaitaire pour frais de
                      recouvrement de 40 € est applicable. Pas d’escompte pour règlement anticipé.
                    </span>
                  </td>
                )}
              </tr>
              {note && (
                <tr>
                  <td
                    style={{ borderColor: baseColor || "#000" }}
                    colSpan={1}
                    className="note-terms"
                  >
                    <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(note || "") }} />
                  </td>
                </tr>
              )}
              {latePaymentPenalty && (
                <tr>
                  <td
                    style={{ borderColor: baseColor || "#000" }}
                    colSpan={1}
                    className="extra-terms"
                  >
                    <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(latePaymentPenalty || "") }} />
                  </td>
                </tr>
              )}
              {fixedRecoveryFee && (
                <tr>
                  <td
                    style={{ borderColor: baseColor || "#000" }}
                    colSpan={1}
                    className="extra-terms"
                  >
                    <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(fixedRecoveryFee || "") }} />
                  </td>
                </tr>
              )}

              {discount && (
                <tr>
                  <td
                    style={{ borderColor: baseColor || "#000" }}
                    colSpan={1}
                    className="extra-terms"
                  >
                    <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(discount || "") }} />
                  </td>
                </tr>
              )}
              {isIntreprenneur && (
                <tr>
                  <td
                    style={{ borderColor: baseColor || "#000" }}
                    colSpan={1}
                    className="extra-terms"
                  >
                    <span>TVA non applicable, article 293 B du CGI.</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      <footer className="default-footer" style={{ color: baseTextColor || "#000" }}>
        <div className="footer-container">
          <div className="generated-by" style={{ borderBottomColor: baseColor || "#000" }}>
            Document générè par NeoFact by
            <span className="newrai-icon">
              <img src={generateByLogo ?? undefined} alt={accountingFirmName ?? undefined} />
            </span>
          </div>
          <hr
            style={{
              backgroundColor: baseColor || "#000",
              border: "2px solid transparent",
              margin: "0",
              padding: "0",
            }}
          />
          <small className="sender-info-dimmable" style={{ color: baseTextColor || "#000" }}>
            {company?.displayName || company?.name} • {address?.address1 || ""} •{" "}
            {address?.postalCode || ""} {address?.city || ""} {address?.country?.name || ""} • Tva
            Intracom: {company?.euVatNumber} • Siret: {company?.vatNumber} • Code Ape:{" "}
            {company?.businessActivity?.code} • Forme Juridique: {company?.legalStructure?.code} •{" "}
            {company?.email} • {company?.phone1} • {company?.website}
          </small>
        </div>
      </footer>
    </div>
  );
}
